import React from 'react';
import { Box, Container, Grid, Stack, Typography, Paper, Card, CardContent, Button, Link } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import MovieClip from '../MovieClips';
import servicerobot from '../images/service_robot.png';
import styledrobot from '../images/service_robot_styled.png';
import lastmile from '../images/last_mile_robot.png';
import Neuron from '../components/neuron';

const robotImages = [
  { src: servicerobot, alt: 'Service Robot', label: 'Service Robot' },
  { src: styledrobot, alt: 'Styled Robot', label: 'Styled Robot' },
  { src: lastmile, alt: 'Last Mile Robot', label: 'Last Mile Delivery' },
];

const features = [
  {
    id: 'robots',
    title: 'Smart Robots for Hospitality',
    description: 'At GoBotic, we bring intelligent automation to the world of hospitality. Our state-of-the-art service robots are designed specifically for hotels and restaurants, streamlining room deliveries and guest interaction and food service. Whether it\'s enhancing efficiency, cutting operational costs, or elevating the guest experience, GoBotic delivers precision, reliability, and a touch of futuristic charm. Let your staff focus on what they do best, while we handle the rest.',
    icon: '🤖',
    color: '#2563EB',
  },
  {
    id: 'ai',
    title: 'Last Mile Delivery Robots',
    description: 'Our semi-autonomous last mile delivery robots are engineered to navigate urban environments with ease, ensuring timely and efficient deliveries. Equipped with advanced navigation systems and AI-driven decision-making, these robots can handle various terrains and weather conditions, making them ideal for both urban and suburban settings. With a focus on safety and reliability, our delivery robots are the perfect solution for businesses looking to enhance their logistics operations. Creating "Full Self Driving" is a complex task, but we are committed to making it a reality. Our robots are designed to adapt and learn from their environment, ensuring they can handle the challenges of real-world delivery scenarios.',
    icon: '🚗',
    color: '#7C3AED',
  },
  {
    id: 'security',
    title: 'Face Recognition and Security',
    description: 'Automated identity tracking in secure environments. In today\'s world, security is paramount. Our face recognition technology leverages advanced AI algorithms to provide accurate and reliable identification, enhancing security measures for businesses and public spaces. Coupled with our robust vector database, we ensure that your security systems are not only effective but also efficient in processing and retrieving data. Trust GoBotic to safeguard your assets with cutting-edge technology.',
    icon: '🔒',
    color: '#059669',
  },
];

export const Main = () => {
  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '50vh', md: '60vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
          }}
        >
          <Neuron height="100%" background="#000000" />
        </Box>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography variant="h2" component="h1" sx={{ mb: 2, fontWeight: 700 }}>
            Intelligent Automation for a{' '}
            <Box component="span" sx={{ color: 'primary.main' }}>Smarter Future</Box>
          </Typography>
          <Typography variant="h5" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
            Robotics and AI solutions for hospitality, logistics, and security.
          </Typography>
        </Container>
      </Box>

      {/* Robot Showcase */}
      <Container maxWidth="100%" sx={{ py: 2 }}>
        <Stack spacing={1} sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
            Our Robots
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
            Precision-engineered robots designed to integrate seamlessly into your operations.
          </Typography>
        </Stack>
        <Grid container spacing={4} justifyContent="center">
          {robotImages.map((robot, index) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: 'primary.main',
                    boxShadow: '0 12px 40px rgba(37,99,235,0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 280, sm: 320, md: 380 },
                    bgcolor: 'rgba(255,255,255,0.02)',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={robot.src}
                    alt={robot.alt}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      transition: 'transform 0.5s ease',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  />
                </Box>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {robot.label}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Stack spacing={1} sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
              Capabilities
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
              End-to-end solutions that power the next generation of autonomous systems.
            </Typography>
          </Stack>
          <Grid container spacing={4}>
            {features.map((feature) => (
              <Grid item xs={12} md={4} key={feature.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'background.default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        mb: 3,
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, flex: 1 }}>
                      {feature.description}
                    </Typography>
                    <Button
                      size="small"
                      endIcon={<ArrowForward />}
                      sx={{ mt: 3, alignSelf: 'flex-start', color: 'primary.main' }}
                    >
                      Learn more
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Video Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Stack spacing={1} sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
            See It In Action
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Watch our service robot operating in a real hotel environment.
          </Typography>
        </Stack>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'black',
            aspectRatio: '9/16',
            maxWidth: 400,
            mx: 'auto',
            width: '100%',
          }}
        >
          <Box sx={{ width: '100%', height: '100%' }}>
            <MovieClip />
          </Box>
        </Paper>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(124,58,237,0.15) 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
            Ready to Automate?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 500, mx: 'auto' }}>
            Let\'s discuss how GoBotic can transform your operations with intelligent robotics and AI.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" href="/contact" component={Link}>
              Get in Touch
            </Button>
            <Button variant="outlined" size="large" href="/vbase" component={Link}>
              Explore VBASE
            </Button>
          </Stack>
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          py: 4,
          mt: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              GOBOTIC © 2025. GOBOS - This website and the Gobotic Operating System - by Karl Lilje.
            </Typography>
            <Stack direction="row" spacing={3}>
              {['Home', 'About', 'Contact', 'VBASE'].map((item) => (
                <Typography
                  key={item}
                  component={Link}
                  to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};
